/**
 * useOvertimeTypes
 * Shared hook that fetches overtime types based on screen data mode.
 * - Live mode: fetches from the OvertimeTypes API
 * - Demo mode: returns SEEDED_OVERTIME_TYPES (mock data)
 *
 * Used by TimesheetGrid, ApprovalDashboard, SettingsPage, and TimesheetReportsPage.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { getOvertimeTypes } from '@/services/api/overtimeTypesApi';
import { SEEDED_OVERTIME_TYPES } from '@/data/mockOvertimeData';
import type { OvertimeType } from '@/types/timesheetSettings';

interface UseOvertimeTypesOptions {
  /** When true, also returns inactive (soft-deleted) types. Default: false */
  includeInactive?: boolean;
  /** When false, skip fetching entirely (e.g. when overtime is disabled). Default: true */
  enabled?: boolean;
  /**
   * Screen ID used to determine demo vs live mode (e.g. 'timesheet', 'approvals').
   * When omitted the hook falls back to a sensible default ('settings-general').
   */
  screenId?: string;
}

interface UseOvertimeTypesResult {
  /** All fetched overtime types (including inactive if requested) */
  overtimeTypes: OvertimeType[];
  /** Convenience: only the active types */
  activeTypes: OvertimeType[];
  isLoading: boolean;
  error: string | null;
  /** Re-fetch types from the API (no-op in demo mode) */
  refetch: () => Promise<void>;
}

export function useOvertimeTypes(options?: UseOvertimeTypesOptions): UseOvertimeTypesResult {
  const { includeInactive = false, enabled = true, screenId = 'settings-general' } = options ?? {};
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.id;
  const { getScreenMode } = useDataSource();
  const mode = getScreenMode(screenId);
  const isLive = mode === 'live';

  const [overtimeTypes, setOvertimeTypes] = useState<OvertimeType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTypes = useCallback(async () => {
    if (!enabled) {
      setOvertimeTypes([]);
      return;
    }

    // Demo mode — return seeded mock data immediately
    if (!isLive) {
      setOvertimeTypes(SEEDED_OVERTIME_TYPES);
      setError(null);
      return;
    }

    // Live mode — fetch from API
    if (!companyId) {
      setOvertimeTypes([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const types = await getOvertimeTypes(companyId, includeInactive);
      setOvertimeTypes(types);
    } catch (err) {
      console.error('Failed to fetch overtime types:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch overtime types');
      // Keep existing types on error so the UI doesn't flash empty
    } finally {
      setIsLoading(false);
    }
  }, [companyId, includeInactive, enabled, isLive]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  // Re-fetch when company changes (live mode only).
  // Debounced to coalesce rapid switches and avoid stale-context 403s.
  useEffect(() => {
    if (!isLive) return;
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(() => {
        if (signal?.aborted) return;
        fetchTypes();
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [fetchTypes, isLive]);

  const activeTypes = useMemo(() => overtimeTypes.filter(t => t.active), [overtimeTypes]);

  return {
    overtimeTypes,
    activeTypes,
    isLoading,
    error,
    refetch: fetchTypes,
  };
}
